import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../firebase/auth.tsx";
import "../App.css";
import "../styles/main.css";
import styles from "../styles/headsetList.module.css";
import ConfirmDeleteHospitalModal from "./modals/confirmDeleteHospitalModal.tsx";
import ChangeHospitalDataModal from "./modals/changeHospitalDataModal.tsx";
import CreateHospitalModal from "./modals/createHospitalModal.tsx"; // CreateHospitalModal is used for global admin
import { getHospitalsInHeadquarter, getAllHospitals, getUsersInGroup } from "./helpers/requests.tsx";
import { useTableFilters } from "./hooks/useTableFilters.tsx";
import TableHeaderFilter from "./common/TableHeaderFilter.tsx";

import { useTranslation } from "react-i18next";

const HospitalList = ({ hospitals, setHospitals }) => {
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const { userId, userRole } = useAuth();
  const [isLoadingHospitals, setIsLoadingHospitals] = useState(false);
  const [selectedHospitalId, setSelectedHospitalId] = useState("");
  const [selectedHospitalName, setSelectedHospitalName] = useState("");
  const [selectedHospitalCountryId, setSelectedHospitalCountryId] = useState("");
  const [selectedHospitalCountryCode, setSelectedHospitalCountryCode] = useState("");
  const [editingHospitals, setEditingHospitals] = useState<{
    [key: string]: boolean;
  }>({});
  const [showChangeDataModal, setShowChangeDataModal] = useState(false);
  const [showCreateHospitalModal, setShowCreateHospitalModal] = useState(false); // Ensure this is used for Global Admin create button

  const [hospitalUsers, setHospitalUsers] = useState<any[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  const navigate = useNavigate();
  const { headquarterId } = useParams();

  const [showConfirmDeleteHospitalModal, setShowConfirmDeleteHospitalModal] =
    useState(false);

  const { t } = useTranslation();

  // Filter hook
  const {
    filteredData: filteredHospitals,
    filterPanels,
    filters,
    searchQueries,
    panelRefs,
    toggleFilterPanel,
    handleFilterChange,
    handleFilterSearch,
    setFilters
  } = useTableFilters(hospitals || []);

  const refreshHospitals = async () => {
    setIsLoadingHospitals(true);
    let hospitalData = [];
    if (headquarterId) {
      hospitalData = await getHospitalsInHeadquarter(headquarterId);
    } else if (userRole === 'admin') {
       try {
          hospitalData = await getAllHospitals();
       } catch(e) {
           console.error("Failed to fetch all hospitals", e);
       }
    }
    setHospitals(hospitalData || []);
    setIsLoadingHospitals(false);
  };


  useEffect(() => {
    refreshHospitals();

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOptionsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);

      setHospitals([]);
      setSelectedHospitalId("");
      setSelectedHospitalName("");
      setSelectedHospitalCountryId("");
      setSelectedHospitalCountryCode("");
      setEditingHospitals({});
      setHospitalUsers([]);
    };
  }, [setHospitals, headquarterId, userRole]);

  const fetchUsersForHospital = async (hospitalId: string) => {
    setIsLoadingUsers(true);
    try {
      const users = await getUsersInGroup(hospitalId);
      setHospitalUsers(users);
    } catch (error) {
      console.error("Error fetching users for hospital:", error);
      setHospitalUsers([]);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const toggleDropdown = () => {
    setIsOptionsOpen(!isOptionsOpen);
  };

  const handleCreateHospital = async (newHospital: any) => {
    await refreshHospitals();
    setShowCreateHospitalModal(false);
  };

  const renderCreateHospitalModal = () => {
    return (
      <CreateHospitalModal
        setShowCreateHospitalModal={setShowCreateHospitalModal}
        headquarterId={headquarterId} 
        handleCreateHospital={handleCreateHospital}
        showSelectHQ={userRole === 'admin' && !headquarterId}
      />
    );
  };

  const renderChangeDataModal = () => {
    return (
      <ChangeHospitalDataModal
        setShowChangeDataModal={setShowChangeDataModal}
        hospitalId={selectedHospitalId}
        hospitalName={selectedHospitalName}
        hospitalCountryId={selectedHospitalCountryId}
        hospitalCountryCode={selectedHospitalCountryCode}
        headquarterId={headquarterId}
        handleChangeData={handleChangeData}
      />
    );
  };

  const renderConfirmDeleteHospitalModal = () => {
    return (
      <ConfirmDeleteHospitalModal
        setShowConfirmDeleteHospitalModal={setShowConfirmDeleteHospitalModal}
        hospitalId={selectedHospitalId}
        hospitalName={selectedHospitalName}
        hospitalCountryCode={selectedHospitalCountryCode}
        handleDeleteHospitalSubmit={handleDeleteHospitalSubmit}
      />
    );
  };

  const handleDeleteHospitalSubmit = async () => {
    setHospitals((prevHospitals) =>
      prevHospitals.filter((hospital) => hospital.id !== selectedHospitalId)
    );
    setSelectedHospitalId("");
    setSelectedHospitalName("");
    setSelectedHospitalCountryId("");
    setSelectedHospitalCountryCode("");
    setEditingHospitals({});
    setHospitalUsers([]);
    refreshHospitals(); 
  };

  const handleChangeData = async (
    newHospitalName: string,
    newHospitalCountryId: string,
    newHospitalCountryCode: string
  ) => {
    setHospitals((prevHospitals) =>
      prevHospitals.map((hospital) => {
        if (hospital.id === selectedHospitalId) {
          return {
            ...hospital,
            name: newHospitalName,
            countryCode: newHospitalCountryCode,
          };
        }
        return hospital;
      })
    );

    handleEditClick(
      selectedHospitalId,
      newHospitalName,
      newHospitalCountryId,
      newHospitalCountryCode
    );

    setShowChangeDataModal(false);

    setSelectedHospitalName(newHospitalName);
    setSelectedHospitalCountryId(newHospitalCountryId);
    setSelectedHospitalCountryCode(newHospitalCountryCode);
  };

  const handleEditClick = async (
    hospitalId: string,
    hospitalName: string,
    hospitalCountryId: string,
    hospitalCountryCode: string
  ) => {
    setEditingHospitals((prevState) => {
      const newState = { ...prevState };
      Object.keys(newState).forEach((key) => {
        newState[key] = false;
      });
      newState[hospitalId] = true;
      return newState;
    });

    setSelectedHospitalId(hospitalId);
    setSelectedHospitalName(hospitalName);
    setSelectedHospitalCountryId(hospitalCountryId);
    setSelectedHospitalCountryCode(hospitalCountryCode);

    fetchUsersForHospital(hospitalId);
  };

  return (
    <>
      <div className={styles.adminContainer}>
        {isLoadingHospitals ? (
          <p className={styles.loading}>{t("Loading...")}</p>
        ) : (
          <>
            {/* Left side */}
            <div className={styles.leftContainer}>
              <div className={styles.tableHeader}>
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">
                        <TableHeaderFilter
                          column="name"
                          title={t("Hospital")}
                          data={hospitals || []}
                          filterPanels={filterPanels}
                          filters={filters}
                          searchQueries={searchQueries}
                          panelRefs={panelRefs}
                          toggleFilterPanel={toggleFilterPanel}
                          handleFilterChange={handleFilterChange}
                          handleFilterSearch={handleFilterSearch}
                          setFilters={setFilters}
                        />
                      </th>
                      <th scope="col"></th>
                      <th scope="col" className={styles.usersColumn}>
                        {t("Users")}
                      </th>
                      <th scope="col">
                        <TableHeaderFilter
                          column="countryCode"
                          title={t("Country")}
                          data={hospitals || []}
                          filterPanels={filterPanels}
                          filters={filters}
                          searchQueries={searchQueries}
                          panelRefs={panelRefs}
                          toggleFilterPanel={toggleFilterPanel}
                          handleFilterChange={handleFilterChange}
                          handleFilterSearch={handleFilterSearch}
                          setFilters={setFilters}
                          getLocalizedValue={(val) => t("country_" + val)}
                        />
                      </th>
                      <th scope="col" className={styles.editButtonWrapper}></th>
                    </tr>
                  </thead>
                </table>
              </div>

              <div className={styles.tableContent}>
                <div className={`container-fluid ${styles.containerFluid}`}>
                  <table className="table">
                    <tbody>
                      {filteredHospitals && filteredHospitals.map((hospital: any, index: number) => (
                        <tr
                          key={hospital.id}
                          style={{
                            backgroundColor:
                              index % 2 === 0 ? "#D4E5E2" : "transparent",
                          }}
                        >
                          <td>{hospital.name}</td>
                          <td>
                          </td>
                          <td className={styles.usersColumn}>
                            {hospital.groupUsers}
                          </td>
                          <td>{t("country_" + hospital.countryCode)}</td>
                          <td className={styles.editButtonWrapper}>
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={() =>
                                handleEditClick(
                                  hospital.id,
                                  hospital.name,
                                  hospital.countryId,
                                  hospital.countryCode
                                )
                              }
                              style={{
                                backgroundColor: editingHospitals[hospital.id]
                                  ? "#404040"
                                  : "#39A686",
                                borderColor: editingHospitals[hospital.id]
                                  ? "#404040"
                                  : "#39A686",
                              }}
                            >
                              {t("Details")}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            {/* Right side */}
            <div className={styles.rightContainer}>

              {selectedHospitalId ? (
                <>
                  <div className={styles.containerHeaderWrapper}>
                    <div className={styles.containerHeader}>
                      <img
                        src="/images/Icon_Hospital.png"
                        style={{
                          width: "5vh",
                          minWidth: "25px",
                          marginRight: "15px",
                        }}
                      />
                      <div className={styles.userDataWrapper}>
                        <div className={styles.userDataName}>
                          {selectedHospitalName}
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                      }}
                    >
                      <div className="dropdown" ref={dropdownRef}>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={toggleDropdown}
                          style={{
                            width: "13vh",
                            minWidth: "60px",
                            backgroundColor: "#39A686",
                            borderColor: "#39A686",
                            borderRadius: "10px",
                            fontSize: "1em",
                            fontFamily: "VirtualLabQuickSandLight",
                            fontWeight: "bold",
                            paddingRight: "30px",
                          }}
                        >
                          <span
                            style={{
                              marginRight: "1.8vh",
                            }}
                          >
                            &#9013;
                          </span>
                          {t("Options")}
                        </button>
                        {isOptionsOpen && (
                          <div className={styles.dropdownMenu}>
                            <button
                              type="button"
                              onClick={() => {
                                setIsOptionsOpen(false);
                                setShowChangeDataModal(true);
                              }}
                              className="btn"
                            >
                              <img src="/images/Icon_Edit.png" />
                              {t("Edit_Hospital")}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setIsOptionsOpen(false);
                                setShowConfirmDeleteHospitalModal(true);
                              }}
                              className="btn"
                            >
                              <img src="/images/Icon_Delete.png" />
                              {t("Delete_Hospital")}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className={`${styles.trainingsContainer} thin-scrollbar`}>
                    {isLoadingUsers ? (
                      <p>{t("Loading...")}</p>
                    ) : (
                      <ul style={{ listStyleType: "none", padding: 0 }}>
                        {hospitalUsers.map((user) => (
                          <li key={user.id} style={{ marginBottom: "5px" }}>
                            {user.firstName} {user.lastName}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </>
              ) : (
                <p className={styles.selectUser}>{t("Select_hospital_to_edit")}</p>
              )}
            </div>
          </>
        )}
        <div className={styles.adminContainer}>
          {(showConfirmDeleteHospitalModal && renderConfirmDeleteHospitalModal()) ||
            (showCreateHospitalModal && renderCreateHospitalModal()) ||
            (showChangeDataModal && renderChangeDataModal())}
        </div>
      </div>
    </>
  );
};
export default HospitalList;